import { FilesUploader } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { DirectUploadMedia } from "libs/directUpload";
import {
  useGetCurrentCompany,
  useUpdateCompanySettings
} from "modules/companyAdminAuth";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, FormField, Input, MediaFilePicker } from "ui";

import styles from "./CompanySettings.module.scss";

interface SettingsFormValues {
  name: string;
  email: string;
  phoneNumber: string;
  capacity: number | "";
  address: string;
  avatarMediaId: string | null;
  password: string;
  repeatPassword: string;
}

const CompanySettings = () => {
  const { t } = useTranslation();
  const { company } = useGetCurrentCompany();

  const initialAvatar: DirectUploadMedia | null = company?.avatar
    ? (company.avatar as unknown as DirectUploadMedia)
    : null;

  const [avatarMedia, setAvatarMedia] = useState<DirectUploadMedia | null>(
    initialAvatar
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<SettingsFormValues>({
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      capacity: "",
      address: "",
      avatarMediaId: null,
      password: "",
      repeatPassword: ""
    }
  });

  const passwordValue = watch("password");

  useEffect(() => {
    if (company) {
      reset({
        name: company.name ?? "",
        email: company.email ?? "",
        phoneNumber: company.phoneNumber ?? "",
        capacity: company.capacity ?? 0,
        address: company.address ?? "",
        avatarMediaId: company.avatar?.id ?? null,
        password: "",
        repeatPassword: ""
      });
      setAvatarMedia(initialAvatar);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const { mutate, isLoading } = useUpdateCompanySettings({
    onSuccess: () => {
      toast.success(t("Saved"));
      setValue("password", "");
      setValue("repeatPassword", "");
    },
    onError: (e) => toast.error(e?.message || t("Failed to save"))
  });

  const onAvatarUploaded = (media: DirectUploadMedia) => {
    setAvatarMedia(media);
    setValue("avatarMediaId", media.id);
  };

  const onAvatarRemoved = () => {
    setAvatarMedia(null);
    setValue("avatarMediaId", null);
  };

  const onSubmit = (values: SettingsFormValues) => {
    mutate({
      name: values.name,
      email: values.email,
      phoneNumber: values.phoneNumber || undefined,
      capacity: values.capacity === "" ? undefined : Number(values.capacity),
      address: values.address || undefined,
      avatarMediaId: values.avatarMediaId,
      password: values.password ? values.password : undefined
    });
  };

  return (
    <div className={styles.settings}>
      <h2 className={styles.settings__title}>{t("Company Settings")}</h2>

      <form className={styles.settings__card} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.settings__avatarBlock}>
          <FilesUploader
            onSuccessUploading={onAvatarUploaded}
            uploadDirectoryKey={MediaDirectoryKeys.DeliveryCompany}
          >
            {(uploaderProps) => (
              <MediaFilePicker
                allowedMediaTypes={[MediaType.IMAGE]}
                size="md"
                variant="rectangle"
                customSize={{ width: 100, height: 100 }}
                {...uploaderProps}
                value={avatarMedia}
                onDeleteMediaFile={onAvatarRemoved}
              />
            )}
          </FilesUploader>
          <span className={styles.settings__avatarHint}>
            {t("Change photo")}
          </span>
        </div>

        <div className={styles.settings__divider} />

        <div className={styles.settings__row}>
          <FormField
            label={t("Company Name")}
            errorMessage={errors.name?.message}
          >
            <Input
              {...register("name", { required: t("Required field") })}
              placeholder={t("Best Company")}
            />
          </FormField>

          <FormField
            label={t("Contact Phone")}
            errorMessage={errors.phoneNumber?.message}
          >
            <Input
              {...register("phoneNumber")}
              placeholder="+225 27 22 12 34 56"
            />
          </FormField>
        </div>

        <div className={styles.settings__row}>
          <FormField label={t("Email")} errorMessage={errors.email?.message}>
            <Input
              type="email"
              {...register("email", { required: t("Required field") })}
              placeholder="company@example.com"
            />
          </FormField>

          <FormField
            label={t("Capacity")}
            errorMessage={errors.capacity?.message}
          >
            <Input
              type="number"
              min={0}
              {...register("capacity", { valueAsNumber: true })}
              placeholder="50"
            />
            <span className={styles.settings__hint}>
              {t("Max orders per cycle")}
            </span>
          </FormField>
        </div>

        <FormField label={t("Address")} errorMessage={errors.address?.message}>
          <Input {...register("address")} placeholder={t("Abidjan")} />
        </FormField>

        <div className={styles.settings__row}>
          <FormField
            label={t("New Password")}
            errorMessage={errors.password?.message}
          >
            <Input
              isPassword
              {...register("password", {
                minLength: passwordValue
                  ? { value: 8, message: t("Min 8 characters") }
                  : undefined
              })}
              placeholder={t("Leave empty to keep current")}
            />
          </FormField>

          <FormField
            label={t("Repeat Password")}
            errorMessage={errors.repeatPassword?.message}
          >
            <Input
              isPassword
              {...register("repeatPassword", {
                required: passwordValue ? t("Required field") : false,
                validate: (value) =>
                  !passwordValue ||
                  value === passwordValue ||
                  t("Passwords do not match")
              })}
              placeholder="••••••••"
            />
          </FormField>
        </div>

        <Button type="submit" loading={isLoading}>
          {t("Save changes")}
        </Button>
      </form>
    </div>
  );
};

export default CompanySettings;
